/** Home / edit UI that sits above the MapLibre canvas and must keep wheel scroll local. */
export const MAP_OVERLAY_SELECTOR = "[data-map-overlay]";

/** True when the pointer for this wheel event is over a map UI overlay, not the basemap. */
export function wheelTargetsMapOverlay(event: WheelEvent): boolean {
  if (typeof document === "undefined") return false;
  const hit = document.elementFromPoint(event.clientX, event.clientY);
  return hit instanceof Element && hit.closest(MAP_OVERLAY_SELECTOR) != null;
}

function scrollableY(el: HTMLElement): boolean {
  const oy = getComputedStyle(el).overflowY;
  if (oy !== "auto" && oy !== "scroll") return false;
  return el.scrollHeight > el.clientHeight + 1;
}

/** Nearest ancestor of `node` (up to `root`) that can scroll vertically, else `root` when it scrolls. */
export function overlayWheelScrollRoot(node: EventTarget | null, root: HTMLElement): HTMLElement | null {
  let el: Element | null = node instanceof Element ? node : null;
  while (el && el !== root.parentElement) {
    if (el instanceof HTMLElement && scrollableY(el)) return el;
    if (el === root) break;
    el = el.parentElement;
  }
  return scrollableY(root) ? root : null;
}

/** At a scroll edge, block chaining so the map does not zoom. */
export function shouldPreventOverlayWheelChain(event: WheelEvent, root: HTMLElement): boolean {
  const scroller = overlayWheelScrollRoot(event.target, root);
  if (!scroller) return true;
  const max = scroller.scrollHeight - scroller.clientHeight;
  if (max <= 0) return true;
  const y = scroller.scrollTop;
  const dy = event.deltaY;
  if (dy < 0 && y <= 0) return true;
  if (dy > 0 && y >= max - 1) return true;
  return false;
}

/**
 * Capture-phase wheel on a panel root: keep the gesture on the panel and off MapLibre.
 * Returns a disposer for tests.
 */
export function bindMapOverlayWheelIsolation(root: HTMLElement): () => void {
  const onWheel = (event: WheelEvent) => {
    if (!(event.target instanceof Node) || !root.contains(event.target)) return;
    event.stopPropagation();
    if (shouldPreventOverlayWheelChain(event, root)) event.preventDefault();
  };
  root.addEventListener("wheel", onWheel, { capture: true, passive: false });
  return () => root.removeEventListener("wheel", onWheel, { capture: true });
}
